import { useEffect, useState, useCallback, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { API, fmtDate } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import {
  Sparkle, FilePdf, ArrowsClockwise, CheckCircle, Warning, FileText,
  ListChecks, Microphone, BookOpen, CircleNotch, SpeakerHigh, Play, FilmSlate, Star, PencilSimple, Broadcast, Eye, ChartBar, ShieldCheck, ArrowCounterClockwise, ClockCountdown, XCircle, MagnifyingGlass, X, ListNumbers, Plus, Trash, DotsSixVertical,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SocialClipStudio from "@/components/SocialClipStudio";

const ASSET_META = {
  help_article: { label: "Help Centre article", icon: FileText, md: true },
  quick_reference: { label: "Quick reference", icon: ListChecks, md: true },
  transcript: { label: "Narration transcript", icon: Microphone, md: false },
};

const MD = {
  h1: (p) => <h2 className="text-lg font-bold text-navy mt-4 mb-2" {...p} />,
  h2: (p) => <h3 className="text-base font-bold text-navy mt-4 mb-1.5" {...p} />,
  h3: (p) => <h4 className="text-sm font-bold text-navy mt-3 mb-1" {...p} />,
  p: (p) => <p className="text-sm text-slate-700 leading-relaxed mb-2.5" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 space-y-1 mb-3 text-sm text-slate-700" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 space-y-1 mb-3 text-sm text-slate-700" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-semibold text-navy" {...p} />,
  code: (p) => <code className="bg-slate-100 text-[#C2410C] px-1 py-0.5 rounded text-xs" {...p} />,
};

function StatePill({ state }) {
  const map = {
    none: { t: "No assets", cls: "bg-slate-100 text-slate-600" },
    stale: { t: "Needs refresh", cls: "bg-amber-100 text-amber-800" },
    fresh: { t: "Up to date", cls: "bg-emerald-100 text-emerald-800" },
  };
  const m = map[state] || map.none;
  return <span data-testid={`guide-state-${state}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.cls}`}>{m.t}</span>;
}

export default function LearningStudio() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("help_article");
  const [voice, setVoice] = useState("5ilGInSt5TdxZga0bFnm");
  const [defaultVoice, setDefaultVoice] = useState("5ilGInSt5TdxZga0bFnm");
  const [savingVoice, setSavingVoice] = useState(false);
  const [narrating, setNarrating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [capUrl, setCapUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [video, setVideo] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);

  // How-to walkthroughs panel
  const [view, setView] = useState("guides");
  const [query, setQuery] = useState("");
  const [howto, setHowto] = useState(null);
  const [howtoLoading, setHowtoLoading] = useState(false);
  const [howtoPreview, setHowtoPreview] = useState(null);
  const [gen, setGen] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const [coverage, setCoverage] = useState(null);
  const [allVoices, setAllVoices] = useState([]);
  const [screensStale, setScreensStale] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [renarrating, setRenarrating] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(null);
  const voiceAudioRef = useRef(null);
  const [spotSched, setSpotSched] = useState(null);
  const [staleWatch, setStaleWatch] = useState(null);


  const loadGenStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/generation-status");
      setGen(data);
      return data;
    } catch { return null; }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/analytics");
      const map = {};
      (data.items || []).forEach((i) => { map[i.slug] = i; });
      setAnalytics(map);
    } catch { /* noop */ }
  }, []);

  const [freshness, setFreshness] = useState(null);
  const [healthChecking, setHealthChecking] = useState(false);
  const [rerunning, setRerunning] = useState(false);

  const loadFreshness = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/freshness");
      setFreshness(data);
    } catch { /* noop */ }
  }, []);

  const checkVideoHealth = async () => {
    setHealthChecking(true);
    try {
      const { data } = await api.post("/admin/learning/videos/health-check");
      if (data.storage_unreachable)
        toast.error("The video storage service didn't respond — this is a storage/connection issue, not the individual videos. Try again shortly; if it persists, re-generate the videos.", { duration: 8000 });
      else if (data.broken === 0) toast.success("All walkthrough videos load correctly.");
      else toast.error(`${data.broken} video${data.broken === 1 ? "" : "s"} won't load — re-run them to fix.`);
      loadHealthHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run the check"); }
    finally { setHealthChecking(false); }
  };

  const [healthHistory, setHealthHistory] = useState(null);
  const [checkingStorage, setCheckingStorage] = useState(false);
  const [storageHealth, setStorageHealth] = useState(null);
  const loadHealthHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/videos/health-history");
      setHealthHistory(data);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { loadHealthHistory(); }, [loadHealthHistory]);

  const checkStorageHealth = async () => {
    setCheckingStorage(true);
    try {
      const { data } = await api.get("/admin/learning/videos/storage-health");
      setStorageHealth(data);
      if (!data.init_ok)
        toast.error(`Storage didn't authenticate (init ${data.init_status ?? "—"}). This is a key/host issue, not the videos.`, { duration: 8000 });
      else if (data.sample_status === undefined)
        toast.success("Storage connected. No sample video on file to test-fetch.");
      else if (data.sample_ok)
        toast.success("Storage connected and a real video fetched OK — the files are present.");
      else
        toast.error(`Storage connected, but fetching a stored video returned ${data.sample_status}. ${data.sample_status === 404 ? "The files aren't in this environment's bucket — re-run \u201cGenerate everything\u201d." : data.sample_status === 402 ? "Billing/access is suspended for storage." : data.sample_status === 439 ? "Storage quota exceeded — contact support." : "Unexpected status."}`, { duration: 10000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run the storage check"); }
    finally { setCheckingStorage(false); }
  };

  const [testingNarration, setTestingNarration] = useState(false);
  const testNarration = async () => {
    setTestingNarration(true);
    try {
      const { data } = await api.get("/admin/learning/narration/test");
      if (data.ok) toast.success(data.message);
      else toast.error(data.message || "ElevenLabs narration test failed", { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not test the narration key"); }
    finally { setTestingNarration(false); }
  };

  const [stopping, setStopping] = useState(false);
  const stopGeneration = async () => {
    setStopping(true);
    try {
      const { data } = await api.post("/admin/learning/cancel");
      toast.success(`Stopped — cleared ${data.cleared} in-progress item${data.cleared === 1 ? "" : "s"}. You can start clean now.`);
      loadGenStatus();
      loadHowto();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not stop generation"); }
    finally { setStopping(false); }
  };

  const rerunFailed = async () => {
    setRerunning(true);
    try {
      await api.post("/admin/learning/rerun-failed");
      toast.info("Re-running the walkthroughs that aren't ready…");
      loadGenStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setRerunning(false); }
  };

  useEffect(() => { loadFreshness(); }, [loadFreshness]);

  useEffect(() => { loadGenStatus(); loadAnalytics(); }, [loadGenStatus, loadAnalytics]);

  useEffect(() => {
    if (!gen?.busy) return;
    const t = setTimeout(async () => {
      const d = await loadGenStatus();
      if (d && !d.busy) { loadHowto(); loadAnalytics(); loadFreshness(); }
    }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gen, loadGenStatus, loadAnalytics]);

  const generateEverything = async () => {
    if (!window.confirm("Build every walkthrough video (5 guides + 18 how-tos) that's missing? This can take several minutes.")) return;
    try {
      await api.post("/admin/learning/generate-all", { only_missing: true });
      toast.info("Generating all walkthroughs — this runs in the background.");
      loadGenStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };
  const [scriptEdit, setScriptEdit] = useState(null); // {slug, title}
  const [scriptText, setScriptText] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptSaving, setScriptSaving] = useState(false);
  const [scriptPolishing, setScriptPolishing] = useState(false);

  const [chapterEdit, setChapterEdit] = useState(null); // {slug, title}
  const [chapterList, setChapterList] = useState([]);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [chapterSaving, setChapterSaving] = useState(false);
  const dragIndexRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

  const parseTime = (str) => {
    const s = String(str || "").trim();
    if (s.includes(":")) {
      const [m, sec] = s.split(":");
      return (parseInt(m, 10) || 0) * 60 + (parseFloat(sec) || 0);
    }
    return parseFloat(s) || 0;
  };
  const fmtTime = (t) => {
    const sec = Math.max(0, Math.round((t || 0) * 10) / 10);
    const m = Math.floor(sec / 60);
    const r = (sec - m * 60).toFixed(0).padStart(2, "0");
    return `${m}:${r}`;
  };

  const openChapters = async (g) => {
    setChapterEdit({ slug: g.slug, title: g.title, kind: "howto" });
    setChapterLoading(true);
    setChapterList([]);
    try {
      const { data } = await api.get(`/admin/learning/howto/${g.slug}/chapters`);
      setChapterList((data.chapters || []).map((c) => ({ ...c, _t: fmtTime(c.time), _t0: fmtTime(c.time), _orig: c.time, _id: uid() })));
    } catch { toast.error("Could not load chapters"); }
    finally { setChapterLoading(false); }
  };
  const openMktChapters = async (key, title) => {
    setChapterEdit({ key, title, kind: "marketing" });
    setChapterLoading(true);
    setChapterList([]);
    try {
      const { data } = await api.get(`/admin/marketing/${key}/chapters`);
      setChapterList((data.chapters || []).map((c) => ({ label: c.title, desc: c.desc || "", _t: fmtTime(c.start), _t0: fmtTime(c.start), _orig: c.start, _id: uid() })));
    } catch { toast.error("Could not load chapters (generate the video first)"); }
    finally { setChapterLoading(false); }
  };
  const updateChapter = (i, field, value) =>
    setChapterList((list) => list.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  const removeChapter = (i) => setChapterList((list) => list.filter((_, idx) => idx !== i));
  const addChapter = () => setChapterList((list) => [...list, { label: "", _t: "0:00", _id: uid() }]);
  const moveChapter = (from, to) => {
    if (from == null || to == null || from === to) return;
    setChapterList((list) => {
      const next = [...list];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };
  const saveChapters = async () => {
    setChapterSaving(true);
    try {
      const timeOf = (c) => (c._t === c._t0 && c._orig != null) ? c._orig : parseTime(c._t);
      if (chapterEdit.kind === "marketing") {
        const chapters = chapterList
          .map((c) => ({ title: (c.label || "").trim(), start: timeOf(c), desc: (c.desc || "").trim() }))
          .filter((c) => c.title);
        if (!chapters.length) { toast.error("Add at least one chapter with a title"); setChapterSaving(false); return; }
        await api.put(`/admin/marketing/${chapterEdit.key}/chapters`, { chapters });
      } else {
        const chapters = chapterList
          .map((c) => ({ label: (c.label || "").trim(), time: timeOf(c), screen: c.screen }))
          .filter((c) => c.label);
        if (!chapters.length) { toast.error("Add at least one chapter with a title"); setChapterSaving(false); return; }
        await api.put(`/admin/learning/howto/${chapterEdit.slug}/chapters`, { chapters });
      }
      toast.success("Chapters saved.");
      setChapterEdit(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save chapters"); }
    finally { setChapterSaving(false); }
  };

  const polishScript = async () => {
    if ((scriptText || "").trim().length < 40) { toast.error("Add more text before polishing"); return; }
    setScriptPolishing(true);
    try {
      const { data } = await api.post(`/admin/learning/howto/${scriptEdit.slug}/polish`, { transcript: scriptText });
      setScriptText(data.transcript || scriptText);
      toast.success("Polished — review and save to regenerate.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not polish"); }
    finally { setScriptPolishing(false); }
  };

  const openScript = async (g) => {
    setScriptEdit({ slug: g.slug, title: g.title });
    setScriptLoading(true);
    setScriptText("");
    try {
      const { data } = await api.get(`/admin/learning/howto/${g.slug}/transcript`);
      setScriptText(data.transcript || "");
    } catch { toast.error("Could not load the script"); }
    finally { setScriptLoading(false); }
  };

  const saveScript = async () => {
    if ((scriptText || "").trim().length < 40) { toast.error("Please write at least a couple of sentences"); return; }
    setScriptSaving(true);
    try {
      await api.post(`/admin/learning/howto/${scriptEdit.slug}/transcript`, { transcript: scriptText });
      toast.success("Script saved — regenerating narration and video.");
      setHowto((h) => h && { ...h, guides: h.guides.map((x) => x.slug === scriptEdit.slug ? { ...x, status: "generating" } : x) });
      setScriptEdit(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setScriptSaving(false); }
  };

  const loadHowto = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/howto/status");
      setHowto(data);
      return data;
    } catch { toast.error("Could not load how-to videos"); return null; }
  }, []);

  const loadCoverage = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/coverage");
      setCoverage(data);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    if (view !== "howto") return;
    setHowtoLoading(true);
    loadHowto().finally(() => setHowtoLoading(false));
    loadCoverage();
  }, [view, loadHowto, loadCoverage]);

  useEffect(() => {
    if (view !== "howto" || !howto) return;
    const busy = (howto.guides || []).some((g) => g.status === "generating");
    if (!busy) return;
    const t = setTimeout(() => { loadHowto(); loadCoverage(); }, 5000);
    return () => clearTimeout(t);
  }, [view, howto, loadHowto, loadCoverage]);

  const regenHowto = async (slug) => {
    try {
      await api.post(`/admin/learning/howto/${slug}/regenerate`);
      toast.info("Regenerating walkthrough — about a minute.");
      setHowto((h) => h && { ...h, guides: h.guides.map((g) => g.slug === slug ? { ...g, status: "generating" } : g) });
      loadCoverage();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not regenerate"); }
  };

  const buildGap = async (guideSlug) => {
    if (!guideSlug) return;
    await regenHowto(guideSlug);
    setCoverage((c) => c && {
      ...c,
      screens: c.screens.map((s) => s.guide_slug === guideSlug ? { ...s, status: "generating" } : s),
      gaps: c.gaps.map((s) => s.guide_slug === guideSlug ? { ...s, status: "generating" } : s),
    });
  };

  const fillGaps = async () => {
    try {
      const { data } = await api.post("/admin/learning/coverage/fill-gaps");
      if (data.count === 0) { toast.success(data.message || "Every screen is already covered."); return; }
      toast.info(`Building ${data.count} walkthrough${data.count === 1 ? "" : "s"} to close every gap…`);
      loadHowto();
      loadCoverage();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };

  const spotlightHowto = async (slug) => {
    try {
      const { data } = await api.post(`/admin/learning/howto/${slug}/spotlight`);
      setHowto((h) => h && { ...h, guides: h.guides.map((g) => (g.slug === slug ? { ...g, spotlight: data.spotlight } : g)) });
      toast.success(data.spotlight ? "Added to Learning Centre spotlight" : "Removed from spotlight");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update spotlight"); }
  };


  const featureHowto = async (slug) => {
    try {
      const { data } = await api.post(`/admin/learning/howto/${slug}/feature`);
      setHowto((h) => h && { ...h, guides: h.guides.map((g) => ({ ...g, featured: g.slug === slug ? data.featured : false })) });
      toast.success(data.featured ? "Set as the ‘Start here’ walkthrough" : "Removed from ‘Start here’");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const genMissingHowto = async () => {
    try {
      await api.post("/admin/learning/howto/generate", { only_missing: true });
      toast.info("Generating any missing walkthroughs…");
      loadHowto();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };

  const recaptureHowto = async () => {
    if (!window.confirm("Re-screenshot every app screen and regenerate all 18 how-to videos? This takes several minutes.")) return;
    try {
      await api.post("/admin/learning/howto/recapture");
      toast.info("Recapturing screens and regenerating all walkthroughs…");
      loadHowto();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };

  const fetchVideoStatus = useCallback(async (gid) => {
    try {
      const { data } = await api.get(`/admin/learning/guides/${gid}/video/status`);
      setVideo(data);
      return data;
    } catch { setVideo(null); return null; }
  }, []);

  const fetchMedia = useCallback(async (gid) => {
    try {
      const [a, c] = await Promise.all([
        api.get(`/admin/learning/guides/${gid}/audio`, { responseType: "blob" }),
        api.get(`/admin/learning/guides/${gid}/captions`, { responseType: "blob" }),
      ]);
      setAudioUrl(URL.createObjectURL(a.data));
      setCapUrl(URL.createObjectURL(new Blob([c.data], { type: "text/vtt" })));
    } catch { setAudioUrl(null); setCapUrl(null); }
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    api.get("/admin/learning/guides")
      .then(({ data }) => { setGuides(data.guides || []); if (!selectedId && data.guides?.length) setSelectedId(data.guides[0].id); })
      .catch(() => toast.error("Could not load guides"))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const loadDetail = useCallback((gid) => {
    if (!gid) return;
    setDetailLoading(true);
    setAudioUrl(null); setCapUrl(null);
    api.get(`/admin/learning/guides/${gid}`)
      .then(({ data }) => {
        setDetail(data);
        const first = (data.assets || [])[0];
        setTab(first ? first.asset_type : "help_article");
        setVoice(data.narration?.voice_id || defaultVoice || data.voices?.[0]?.id || "5ilGInSt5TdxZga0bFnm");
        if (data.narration) fetchMedia(gid);
        fetchVideoStatus(gid);
      })
      .catch(() => toast.error("Could not load guide detail"))
      .finally(() => setDetailLoading(false));
  }, [fetchMedia, fetchVideoStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(); }, []); // eslint-disable-line
  useEffect(() => {
    api.get("/admin/learning/voice-default")
      .then(({ data }) => { setDefaultVoice(data.voice_id); setVoice((v) => v || data.voice_id); setAllVoices(data.voices || []); })
      .catch(() => {});
  }, []);
  const loadScreensStale = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning/screens-staleness");
      setScreensStale(data);
    } catch { /* superadmin-only */ }
  }, []);
  useEffect(() => { loadScreensStale(); }, [loadScreensStale]);
  const loadSchedules = useCallback(async () => {
    try {
      const [s, w] = await Promise.all([
        api.get("/admin/learning/spotlight-schedule"),
        api.get("/admin/learning/stale-autowatch"),
      ]);
      setSpotSched(s.data); setStaleWatch(w.data);
    } catch { /* superadmin-only */ }
  }, []);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  const toggleSpotSched = async (enabled) => {
    try {
      const { data } = await api.put("/admin/learning/spotlight-schedule", { enabled, count: spotSched?.count || 2 });
      toast.success(enabled ? "Weekly spotlight rotation on" : "Weekly spotlight rotation off");
      if (data.rotated) loadHowto();
      loadSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const setSpotCount = async (count) => {
    try { await api.put("/admin/learning/spotlight-schedule", { enabled: spotSched?.enabled || false, count }); loadSchedules(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const rotateSpotlightNow = async () => {
    try { const { data } = await api.post("/admin/learning/spotlight-schedule/rotate"); toast.success(`Spotlight rotated to ${data.rotated} walkthrough${data.rotated === 1 ? "" : "s"}`); loadHowto(); loadSchedules(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not rotate"); }
  };
  const toggleStaleWatch = async (enabled) => {
    try { await api.put("/admin/learning/stale-autowatch", { enabled }); setStaleWatch((w) => ({ ...(w || {}), enabled })); toast.success(enabled ? "Nightly auto-rebuild on" : "Nightly auto-rebuild off"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const rebuildStale = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post("/admin/learning/rebuild-stale", { voice_id: voice });
      toast.success(data.stale ? `Rebuilding ${data.stale} video${data.stale === 1 ? "" : "s"}…` : "Everything already matches the current screens.");
      loadGenStatus(); loadHowto();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start rebuild"); }
    finally { setRebuilding(false); }
  };
  const sampleVoice = async (vid) => {
    try {
      if (voiceAudioRef.current) { voiceAudioRef.current.pause(); voiceAudioRef.current = null; }
      setPlayingVoice(vid);
      const { data } = await api.get(`/admin/learning/voice-preview/${vid}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = new Audio(url);
      voiceAudioRef.current = a;
      a.onended = () => { setPlayingVoice(null); URL.revokeObjectURL(url); };
      a.onerror = () => setPlayingVoice(null);
      await a.play();
    } catch { setPlayingVoice(null); toast.error("Could not play a sample of that voice"); }
  };
  const renarrateAll = async () => {
    if (!window.confirm("Re-narrate and re-render EVERY video (5 guides + 24 how-to + 2 marketing) with this voice? This runs in the background and can take ~15 minutes.")) return;
    setRenarrating(true);
    try {
      const { data } = await api.post("/admin/learning/renarrate-all", { voice_id: voice });
      setDefaultVoice(data.voice_id);
      toast.success(`Re-narrating everything with ${data.voice_name}…`);
      loadGenStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start re-narration"); }
    finally { setRenarrating(false); }
  };
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/learning/guides/${selectedId}/generate`, { force: true });
      toast.success(`Generated ${data.count} asset${data.count === 1 ? "" : "s"} from the guide`);
      await loadDetail(selectedId);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/learning/guides/${selectedId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${detail?.guide?.title || "guide"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build PDF"); }
  };

  const pingSitemap = async () => {
    try {
      const { data } = await api.post("/admin/learning/sitemap/ping");
      toast.success(`Notified search engines of ${data.pinged} guide video page(s).`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not notify"); }
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(`/admin/learning/voice-preview/${voice}`, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(res.data));
      audio.onended = () => setPreviewing(false);
      audio.onerror = () => { setPreviewing(false); toast.error("Could not play preview"); };
      await audio.play();
    } catch { setPreviewing(false); toast.error("Could not play preview"); }
  };

  const saveDefaultVoice = async () => {
    setSavingVoice(true);
    try {
      const { data } = await api.put("/admin/learning/voice-default", { voice_id: voice });
      setDefaultVoice(data.voice_id);
      toast.success(`${data.voice_name} is now the default brand voice`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save default voice"); }
    finally { setSavingVoice(false); }
  };

  const narrate = async () => {
    setNarrating(true);    try {
      const { data } = await api.post(`/admin/learning/guides/${selectedId}/narrate`, { voice_id: voice });
      toast.success(`Narration generated · ${data.voice_name}`);
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Narration failed");
    } finally { setNarrating(false); }
  };

  const generateVideo = async () => {
    setVideoBusy(true);
    const gid = selectedId;
    try {
      await api.post(`/admin/learning/guides/${gid}/video`);
      setVideo({ status: "generating" });
      toast.info("Rendering the walkthrough video — this takes about a minute.");
      const poll = async (n) => {
        const s = await fetchVideoStatus(gid);
        if (s?.status === "ready") { toast.success("Walkthrough video ready"); setVideoBusy(false); return; }
        if (s?.status === "failed") { toast.error(s.error || "Video generation failed"); setVideoBusy(false); return; }
        if (n > 40) { setVideoBusy(false); return; }
        setTimeout(() => poll(n + 1), 4000);
      };
      poll(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start video generation");
      setVideoBusy(false);
    }
  };

  const selectedSlug = guides.find((g) => g.id === selectedId)?.slug;

  const toggleFeature = async () => {
    try {
      const { data } = await api.post(`/admin/learning/guides/${selectedId}/video/feature`);
      setVideo((v) => ({ ...(v || {}), featured: data.featured }));
      toast.success(data.featured ? "Set as the ‘Start here’ walkthrough" : "Removed from ‘Start here’");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const assetsByType = Object.fromEntries((detail?.assets || []).map((a) => [a.asset_type, a]));
  const active = assetsByType[tab];
  const anyStale = (detail?.assets || []).some((a) => a.status === "stale");

  const q = query.trim().toLowerCase();
  const matchesQuery = (g) =>
    !q || (g.title || "").toLowerCase().includes(q) || (g.category || "").toLowerCase().includes(q);
  const filteredGuides = guides.filter(matchesQuery);
  const filteredHowto = (howto?.guides || []).filter(matchesQuery);
  const hasAssets = (detail?.assets || []).length > 0;
  const narration = detail?.narration;
  const hasTranscript = (detail?.assets || []).some((a) => a.asset_type === "transcript");

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[#C2410C] text-xs font-bold tracking-wide uppercase mb-1">
          <GraduationCapWrap /> Learning Studio
        </div>
        <h1 className="text-2xl font-bold text-navy">One guide → every learning asset</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Each guide is the single source of truth. Generate its Help article, Quick reference,
          Transcript and PDF from that one document — and see when an asset falls out of date.
        </p>
      </div>

      {/* Live generation progress banner */}
      {gen && gen.busy && (() => {
        const done = gen.job_active ? (gen.built ?? 0) : gen.ready;
        const pct = gen.total ? Math.round((done / gen.total) * 100) : 0;
        return (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="generation-banner">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <CircleNotch size={16} className="animate-spin" />
              <span data-testid="generation-progress-count">Building walkthroughs — {done} of {gen.total} built</span>
              {gen.failed ? <span className="text-red-600">· {gen.failed} failed</span> : null}
            </div>
            <span className="text-xs font-semibold text-amber-700">{pct}%{gen.generating ? ` · ${gen.generating} rendering` : ""}</span>
          </div>
          <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
            <div className="h-full rounded-full transition-all" data-testid="generation-progress-bar" style={{ width: `${pct}%`, background: "#F59E0B" }} />
          </div>
        </div>
        );
      })()}

      {/* Batch failed — show the reason so it can be diagnosed (visible even while busy) */}
      {gen && gen.failed > 0 && (
        <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="generation-failed-banner">
          <div className="flex items-center gap-2 text-sm font-semibold text-rose-900 mb-1">
            <Warning size={16} weight="fill" /> {gen.failed} of {gen.total} walkthroughs failed to generate
          </div>
          {gen.first_error && (
            <p className="text-xs text-rose-800/90 font-mono bg-white/70 border border-rose-200 rounded-lg px-2.5 py-1.5 mt-1 break-words" data-testid="generation-first-error">
              {gen.first_error}
            </p>
          )}
          <p className="text-xs text-rose-700/80 mt-2">
            Common causes: the ElevenLabs narration key is missing or out of credit, or the media renderer couldn't reach object storage. Fix the cause, then use <b>Re-run failed</b>.
          </p>
        </div>
      )}

      {/* Video health history — flags walkthroughs that keep breaking */}
      {storageHealth && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="storage-health-panel">
          <div className="text-sm font-heading font-bold text-navy mb-1.5">Video storage health</div>
          <div className="text-xs text-navy/70 font-mono space-y-0.5">
            <div>init: <b className={storageHealth.init_ok ? "text-positive" : "text-negative"}>{String(storageHealth.init_status)}</b> ({storageHealth.init_ok ? "authenticated" : "failed"})</div>
            {storageHealth.sample_status !== undefined && (
              <div>fetch stored video: <b className={storageHealth.sample_ok ? "text-positive" : "text-negative"}>{String(storageHealth.sample_status)}</b> {storageHealth.sample_ok ? "(files present ✓)" : storageHealth.sample_status === 404 ? "(not in this bucket — regenerate)" : storageHealth.sample_status === 402 ? "(billing suspended)" : storageHealth.sample_status === 439 ? "(quota exceeded)" : ""}</div>
            )}
            {storageHealth.init_error && <div className="text-negative">init error: {storageHealth.init_error}</div>}
          </div>
        </div>
      )}

      {healthHistory && healthHistory.repeat_offenders && healthHistory.repeat_offenders.length > 0 && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="health-history-panel">
          <div className="flex items-center gap-2 text-sm font-semibold text-navy mb-2">
            <ShieldCheck size={16} weight="bold" style={{ color: "#C2410C" }} />
            Video health — walkthroughs that have broken more than once
          </div>
          <div className="flex flex-wrap gap-2" data-testid="repeat-offenders">
            {healthHistory.repeat_offenders.filter((o) => o.breaks > 1).map((o) => (
              <span key={o.slug} data-testid={`repeat-offender-${o.slug}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800">
                {o.title} · {o.breaks}×
              </span>
            ))}
            {healthHistory.repeat_offenders.filter((o) => o.breaks > 1).length === 0 && (
              <span className="text-xs text-slate-500">No repeat failures — everything's been stable across {healthHistory.total_runs} check{healthHistory.total_runs === 1 ? "" : "s"}.</span>
            )}
          </div>
        </div>
      )}

      {/* View toggle: master guides vs in-app how-to walkthroughs */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="studio-view-toggle">
            {[["guides", "Master guides"], ["howto", "How-to walkthroughs"]].map(([k, label]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`studio-tab-${k}`}
                className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${view === k ? "text-white" : "text-slate-600 hover:text-navy"}`}
                style={view === k ? { background: "#C2410C" } : {}}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by title…"
              data-testid="studio-search-input"
              className="w-52 sm:w-64 text-sm rounded-xl border border-slate-200 bg-white pl-9 pr-8 py-2 text-navy placeholder:text-slate-400 focus:outline-none focus:border-[#C2410C]"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="studio-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy" aria-label="Clear search">
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>
        {(
        <div className="flex items-center gap-2">
          <button onClick={generateEverything} disabled={gen?.busy} data-testid="generate-everything-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white px-3 py-2 rounded-xl disabled:opacity-60" style={{ background: "#C2410C" }}>
            <Sparkle size={15} weight="fill" /> Generate everything
          </button>
          {gen && gen.failed > 0 && !gen.busy && (
            <button onClick={rerunFailed} disabled={rerunning} data-testid="rerun-failed-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white px-3 py-2 rounded-xl disabled:opacity-60" style={{ background: "#0E103F" }}>
              {rerunning ? <CircleNotch size={15} className="animate-spin" /> : <ArrowCounterClockwise size={15} weight="bold" />} Re-run {gen.failed} failed
            </button>
          )}
          {gen && gen.busy && (
            <button onClick={stopGeneration} disabled={stopping} data-testid="stop-generation-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white px-3 py-2 rounded-xl disabled:opacity-60" style={{ background: "#E5484D" }}>
              {stopping ? <CircleNotch size={15} className="animate-spin" /> : <XCircle size={15} weight="bold" />} Stop generation
            </button>
          )}
          <button onClick={checkVideoHealth} disabled={healthChecking} data-testid="video-health-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C] disabled:opacity-60">
            {healthChecking ? <CircleNotch size={15} className="animate-spin" /> : <ShieldCheck size={15} weight="bold" />} Check video health
          </button>
          <button onClick={checkStorageHealth} disabled={checkingStorage} data-testid="storage-health-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C] disabled:opacity-60">
            {checkingStorage ? <CircleNotch size={15} className="animate-spin" /> : <ShieldCheck size={15} weight="bold" />} Storage health
          </button>
          <button onClick={testNarration} disabled={testingNarration} data-testid="test-narration-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C] disabled:opacity-60">
            {testingNarration ? <CircleNotch size={15} className="animate-spin" /> : <SpeakerHigh size={15} weight="bold" />} Test narration key
          </button>
          <button onClick={pingSitemap} data-testid="sitemap-ping-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C]">
            <Broadcast size={15} weight="bold" /> Notify search engines
          </button>
        </div>
        )}
      </div>

      {/* Freshness check — guides whose source changed but video/narration is stale */}
      {freshness && freshness.stale > 0 && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="freshness-banner">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
            <ClockCountdown size={16} weight="bold" />
            {freshness.stale} guide{freshness.stale === 1 ? "" : "s"} {freshness.stale === 1 ? "has" : "have"} drifted out of date
          </div>
          <p className="text-xs text-amber-800/90 mb-2">
            The written source changed after the walkthrough was generated. Regenerate to keep videos in step with the docs.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="freshness-items">
            {(freshness.items || []).map((it) => (
              <span key={it.slug} data-testid={`freshness-item-${it.slug}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white border border-amber-200 text-amber-900">
                {it.kind === "howto" ? "How-to" : "Guide"} · {it.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Brand voice — swap the narrator and re-narrate every video in one job */}
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="brand-voice-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <SpeakerHigh size={18} weight="fill" style={{ color: "#C2410C" }} />
            <div>
              <h3 className="text-sm font-bold text-navy">Brand narrator voice</h3>
              <p className="text-xs text-slate-500">Swap the voice and re-narrate every video — 5 guides, 24 how-to and both marketing clips — in one background job.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="brand-voice-select"
              className="bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-[#C2410C] min-w-[220px]">
              {allVoices.map((v) => <option key={v.id} value={v.id}>{v.name}{v.id === defaultVoice ? " ★ default" : ""}</option>)}
            </select>
            <button onClick={() => sampleVoice(voice)} data-testid="voice-preview-selected" title="Play a sample of the selected voice"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-200 text-[#C2410C] hover:bg-orange/10">
              {playingVoice === voice ? <CircleNotch size={15} className="animate-spin" /> : <Play size={15} weight="fill" />}
              Play sample
            </button>
            <button onClick={renarrateAll} disabled={renarrating || gen?.busy} data-testid="renarrate-all-btn"
              title="Re-narrate & re-render every video with the selected voice"
              className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50" style={{ background: "#C2410C" }}>
              {renarrating ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} weight="bold" />}
              Re-narrate everything
            </button>
          </div>
        </div>
      </div>

      {/* Spotlight scheduling — keep the Learning Centre feeling fresh */}
      {spotSched && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="spotlight-schedule-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" style={{ color: "#F37A24" }} />
              <div>
                <h3 className="text-sm font-bold text-navy">Learning Centre spotlight</h3>
                <p className="text-xs text-slate-500">
                  {spotSched.enabled
                    ? `Rotating ${spotSched.count} walkthrough${spotSched.count === 1 ? "" : "s"} every Monday.`
                    : "Feature your best walkthroughs, or auto-rotate them weekly so the Learning Centre always feels fresh."}
                  {spotSched.current?.length > 0 && (
                    <span className="text-slate-400"> · now: {spotSched.current.map((c) => c.title).join(", ")}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select value={spotSched.count} onChange={(e) => setSpotCount(Number(e.target.value))} data-testid="spotlight-count-select"
                className="bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-[#C2410C]">
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} in spotlight</option>)}
              </select>
              <button onClick={rotateSpotlightNow} data-testid="spotlight-rotate-now"
                className="inline-flex items-center gap-1.5 text-navy text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C]">
                <ArrowsClockwise size={15} /> Rotate now
              </button>
              <button onClick={() => toggleSpotSched(!spotSched.enabled)} data-testid="spotlight-schedule-toggle"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border transition-colors ${spotSched.enabled ? "bg-orange/10 border-orange/40 text-orangeink" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {spotSched.enabled ? <CheckCircle size={15} weight="fill" /> : <ClockCountdown size={15} />}
                {spotSched.enabled ? "Weekly rotation on" : "Auto-rotate weekly"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto re-render — videos whose source screenshots are newer than the render */}
      {screensStale && (
        <div className={`mb-5 rounded-2xl border p-4 ${screensStale.stale > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="screens-stale-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ClockCountdown size={16} weight="bold" className={screensStale.stale > 0 ? "text-amber-900" : "text-emerald-700"} />
              {screensStale.stale > 0 ? (
                <div>
                  <div className="text-sm font-semibold text-amber-900" data-testid="screens-stale-count">
                    {screensStale.stale} video{screensStale.stale === 1 ? "" : "s"} built before the UI was last recaptured
                  </div>
                  <p className="text-xs text-amber-800/90">The screenshots changed after these videos rendered — rebuild them so they match the current interface.</p>
                </div>
              ) : (
                <span className="text-sm font-semibold text-emerald-800" data-testid="screens-stale-none">All {screensStale.total} videos match the current screens.</span>
              )}
            </div>
            {screensStale.stale > 0 && (
              <button onClick={rebuildStale} disabled={rebuilding || gen?.busy} data-testid="rebuild-stale-btn"
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50" style={{ background: "#0E103F" }}>
                {rebuilding ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} weight="bold" />}
                Rebuild {screensStale.stale} stale
              </button>
            )}
          </div>
          {staleWatch && (
            <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer" data-testid="stale-autowatch-toggle">
              <input type="checkbox" checked={!!staleWatch.enabled} onChange={(e) => toggleStaleWatch(e.target.checked)}
                className="rounded border-slate-300 text-[#C2410C] focus:ring-[#C2410C]" />
              Auto-rebuild screen-stale videos every night (self-heals after a UI recapture)
              {staleWatch.last_run_at && <span className="text-slate-400">· last run {fmtDate(staleWatch.last_run_at)}</span>}
            </label>
          )}
          {screensStale.stale > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="screens-stale-items">
              {screensStale.items.filter((i) => i.stale).map((it) => (
                <span key={`${it.kind}-${it.slug}`} data-testid={`screens-stale-${it.slug}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white border border-amber-200 text-amber-900">
                  {it.kind === "howto" ? "How-to" : it.kind === "guide" ? "Guide" : "Marketing"} · {it.title}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "howto" ? (
        <div data-testid="howto-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-navy">In-app how-to walkthroughs</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                {howto ? `${howto.ready}/${howto.total} ready` : "…"} · single-screen narrated videos shown in the Learning Centre.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={genMissingHowto} data-testid="howto-generate-missing"
                className="inline-flex items-center gap-2 text-navy text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C]">
                <Sparkle size={15} weight="fill" /> Generate missing
              </button>
              <button onClick={recaptureHowto} data-testid="howto-recapture"
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-xl"
                style={{ background: "#0E103F" }}>
                <ArrowsClockwise size={15} /> Recapture &amp; regenerate all
              </button>
            </div>
          </div>

          {/* Marketing videos — edit the homepage tour / explainer chapter markers */}
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="marketing-chapters-panel">
            <div className="flex items-center gap-2 text-sm font-semibold text-navy mb-1">
              <FilmSlate size={16} weight="fill" style={{ color: "#C2410C" }} /> Marketing videos
            </div>
            <p className="text-xs text-slate-500 mb-3">Reorder, rename or re-time the chapter markers shown on the marketing homepage.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => openMktChapters("tour", "Product tour")} data-testid="mkt-editchapters-product-tour"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 text-navy hover:border-[#C2410C] transition-colors">
                <ListNumbers size={14} /> Product tour chapters
              </button>
              <button onClick={() => openMktChapters("why", "Why Lottie")} data-testid="mkt-editchapters-why-lottie"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 text-navy hover:border-[#C2410C] transition-colors">
                <ListNumbers size={14} /> Why Lottie chapters
              </button>
            </div>
          </div>

          {/* Coverage panel — which app screens still have no walkthrough */}
          {coverage && (
            <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="coverage-panel">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <ShieldCheck size={16} weight="bold" style={{ color: "#C2410C" }} />
                  Screen coverage
                  <span className="text-xs font-medium text-slate-500">
                    {coverage.covered}/{coverage.total} app screens have a ready walkthrough
                  </span>
                </div>
                {coverage.gaps_count === 0 ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="coverage-complete">
                    <CheckCircle size={13} weight="fill" /> Every screen is covered
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="coverage-gap-count">
                      <Warning size={13} weight="fill" /> {coverage.gaps_count} screen{coverage.gaps_count === 1 ? "" : "s"} without a walkthrough
                    </span>
                    {coverage.gaps.some((s) => s.guide_slug && s.status !== "generating") && (
                      <button onClick={fillGaps} data-testid="coverage-fill-gaps"
                        className="inline-flex items-center gap-1.5 text-white text-[11px] font-bold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity"
                        style={{ background: "#C2410C" }}>
                        <Sparkle size={12} weight="fill" /> Fill all gaps
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mb-3">
                <div className="h-full rounded-full transition-all" style={{ width: `${coverage.total ? Math.round((coverage.covered / coverage.total) * 100) : 0}%`, background: "#C2410C" }} />
              </div>
              {coverage.gaps_count > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="coverage-gaps">
                  {coverage.gaps.map((s) => (
                    <span key={s.screen} data-testid={`coverage-gap-${s.screen}`}
                      title={s.status === "none" ? "No how-to guide targets this screen yet" : `Walkthrough status: ${s.status}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold pl-2.5 pr-1.5 py-1 rounded-full bg-white border border-amber-200 text-amber-900">
                      <Warning size={11} weight="fill" /> {s.label}
                      {s.guide_slug ? (
                        s.status === "generating" ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 pl-1" data-testid={`coverage-building-${s.screen}`}>
                            <CircleNotch size={11} className="animate-spin" /> Building…
                          </span>
                        ) : (
                          <button onClick={() => buildGap(s.guide_slug)} data-testid={`coverage-build-${s.screen}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-0.5 rounded-full hover:opacity-90 transition-opacity"
                            style={{ background: "#0E103F" }}>
                            <Sparkle size={10} weight="fill" /> Create walkthrough
                          </button>
                        )
                      ) : (
                        <span className="text-[10px] font-normal text-amber-700/70 pl-1">no guide yet</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {howtoLoading || !howto ? (
            <div className="p-10 text-sm text-slate-500 flex items-center gap-2"><CircleNotch className="animate-spin" /> Loading…</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="howto-grid">
              {filteredHowto.map((g) => (
                <div key={g.slug} data-testid={`howto-card-${g.slug}`}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
                  <button onClick={() => g.status === "ready" && setHowtoPreview(g)}
                    data-testid={`howto-preview-${g.slug}`}
                    className="relative aspect-video bg-navy group disabled:cursor-default"
                    disabled={g.status !== "ready"}>
                    {g.status === "ready" && (
                      <img src={`${API}/learning/howto/${g.slug}/thumbnail`} alt={g.title}
                        className="w-full h-full object-cover" loading="lazy" />
                    )}
                    {g.status === "ready" ? (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-12 h-12 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(243,122,36,0.95)" }}>
                          <Play size={20} weight="fill" className="text-white ml-0.5" />
                        </span>
                      </span>
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center text-white/70 text-sm gap-2"
                        title={g.status === "failed" && g.error ? g.error : undefined}>
                        {g.status === "generating" ? <><CircleNotch size={16} className="animate-spin" /> Rendering…</>
                          : g.status === "failed" ? "Failed" : "Not generated"}
                      </span>
                    )}
                    {g.featured && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white" style={{ background: "#F59E0B" }}>
                        <Star size={11} weight="fill" /> Start here
                      </span>
                    )}
                  </button>
                  <div className="p-4 flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="overline text-orangeink">{g.category}</span>
                      {g.stale && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Out of date</span>}
                    </div>
                    <div className="font-semibold text-navy text-sm leading-snug line-clamp-2 flex-1">{g.title}</div>
                    {analytics[g.slug] && (
                      <div className="flex items-center gap-3 text-[11px] text-slate-500" data-testid={`howto-analytics-${g.slug}`}>
                        <span className="inline-flex items-center gap-1"><Eye size={13} /> {analytics[g.slug].viewers} viewer{analytics[g.slug].viewers === 1 ? "" : "s"}</span>
                        <span className="inline-flex items-center gap-1"><ChartBar size={13} /> {analytics[g.slug].avg_percent}% avg</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <button onClick={() => regenHowto(g.slug)} disabled={g.status === "generating"} data-testid={`howto-regen-${g.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:border-[#C2410C] disabled:opacity-50">
                        {g.status === "generating" ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />} Regenerate
                      </button>
                      <button onClick={() => featureHowto(g.slug)} disabled={g.status !== "ready"} data-testid={`howto-feature-${g.slug}`}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors disabled:opacity-40 ${g.featured ? "bg-amber-50 border-amber-300 text-amber-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        <Star size={13} weight={g.featured ? "fill" : "regular"} /> {g.featured ? "Featured" : "Feature"}
                      </button>
                      <button onClick={() => spotlightHowto(g.slug)} disabled={g.status !== "ready"} data-testid={`howto-spotlight-${g.slug}`}
                        title="Show this walkthrough in the Learning Centre 'Spotlight' strip"
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors disabled:opacity-40 ${g.spotlight ? "bg-orange/10 border-orange/40 text-orangeink" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        <Sparkle size={13} weight={g.spotlight ? "fill" : "regular"} /> {g.spotlight ? "Spotlit" : "Spotlight"}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 pt-0.5">
                      <button onClick={() => openScript(g)} data-testid={`howto-editscript-${g.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy/70 hover:text-orangeink transition-colors">
                        <PencilSimple size={13} /> Edit script
                      </button>
                      <button onClick={() => openChapters(g)} disabled={g.status !== "ready"} data-testid={`howto-editchapters-${g.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy/70 hover:text-orangeink transition-colors disabled:opacity-40 disabled:cursor-default">
                        <ListNumbers size={13} /> Chapters
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredHowto.length === 0 && (
                <div className="col-span-full p-10 text-center text-sm text-slate-500" data-testid="howto-empty">
                  No how-to walkthroughs match “{query}”.
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Guide list */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden self-start" data-testid="learning-guide-list">
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">Guides</div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><CircleNotch className="animate-spin" /> Loading…</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {filteredGuides.map((g) => (
                <button key={g.id} onClick={() => setSelectedId(g.id)} data-testid={`guide-item-${g.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${selectedId === g.id ? "bg-slate-50" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-navy truncate">{g.title}</span>
                    <StatePill state={g.state} />
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {g.category} · {g.assets_generated}/{g.assets_total} assets
                  </div>
                </button>
              ))}
              {filteredGuides.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500" data-testid="guides-empty">
                  No guides match “{query}”.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="min-w-0">
          {detailLoading || !detail ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-sm text-slate-500 flex items-center gap-2">
              <CircleNotch className="animate-spin" /> Loading guide…
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1"><BookOpen size={14} /> Master guide</div>
                    <h2 className="text-lg font-bold text-navy">{detail.guide.title}</h2>
                    <p className="text-xs text-slate-400 mt-0.5">Last updated {fmtDate(detail.guide.updated_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={generate} disabled={generating} data-testid="generate-assets-btn"
                      className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-xl disabled:opacity-60"
                      style={{ background: "#C2410C" }}>
                      {generating ? <CircleNotch size={16} className="animate-spin" /> : (hasAssets ? <ArrowsClockwise size={16} /> : <Sparkle size={16} weight="fill" />)}
                      {generating ? "Generating…" : (hasAssets ? "Regenerate all" : "Generate assets")}
                    </button>
                    <button onClick={downloadPdf} data-testid="download-pdf-btn"
                      className="inline-flex items-center gap-2 text-navy text-sm font-semibold px-4 py-2.5 rounded-xl border border-slate-200 hover:border-slate-300">
                      <FilePdf size={16} /> PDF
                    </button>
                  </div>
                </div>
                {anyStale && (
                  <div data-testid="stale-banner" className="mt-4 flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <Warning size={16} weight="fill" /> The guide changed since these assets were generated — regenerate to bring them up to date.
                  </div>
                )}
              </div>

              {!hasAssets ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="no-assets">
                  <Sparkle size={28} className="mx-auto text-[#C2410C] mb-2" weight="fill" />
                  <p className="text-sm text-slate-600">No assets yet. Click <b>Generate assets</b> to create the Help article, Quick reference and Transcript from this guide.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="flex border-b border-slate-100 flex-wrap">
                    {(detail.assets || []).map((a) => {
                      const M = ASSET_META[a.asset_type] || {};
                      const Icon = M.icon || FileText;
                      return (
                        <button key={a.asset_type} onClick={() => setTab(a.asset_type)} data-testid={`asset-tab-${a.asset_type}`}
                          className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === a.asset_type ? "border-[#C2410C] text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}>
                          <Icon size={16} /> {M.label || a.asset_type}
                          {a.status === "stale"
                            ? <Warning size={13} weight="fill" className="text-amber-500" />
                            : <CheckCircle size={13} weight="fill" className="text-emerald-500" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="p-6" data-testid="asset-content">
                    {active && (
                      <>
                        <div className="flex items-center justify-between mb-3 text-xs text-slate-400">
                          <span>Generated {fmtDate(active.generated_at)} · {active.model}</span>
                          <span className={active.status === "stale" ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                            {active.status === "stale" ? "Out of date" : "Up to date"}
                          </span>
                        </div>
                        {ASSET_META[tab]?.md ? (
                          <div className="max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{active.content}</ReactMarkdown></div>
                        ) : (
                          <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-4 border border-slate-100">{active.content}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* AI narration */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5" data-testid="narration-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <SpeakerHigh size={18} className="text-[#C2410C]" weight="fill" />
                    <h3 className="text-base font-bold text-navy">AI narration</h3>
                    {narration && (
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${narration.status === "stale" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {narration.status === "stale" ? "Out of date" : "Up to date"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="voice-select"
                      className="bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-[#C2410C]">
                      {(detail.voices || []).map((v) => <option key={v.id} value={v.id}>{v.name}{v.id === defaultVoice ? " ★ default" : ""}</option>)}
                    </select>
                    <button onClick={previewVoice} disabled={previewing} data-testid="preview-voice-btn" title="Hear a short sample of this voice"
                      className="inline-flex items-center gap-1.5 text-navy text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C] disabled:opacity-50">
                      {previewing ? <CircleNotch size={15} className="animate-spin" /> : <Play size={15} weight="fill" />}
                      Preview
                    </button>
                    <button onClick={saveDefaultVoice} disabled={savingVoice || voice === defaultVoice} data-testid="set-default-voice-btn"
                      title={voice === defaultVoice ? "This is already the default brand voice" : "Make this the default voice for every new narration"}
                      className="inline-flex items-center gap-1.5 text-navy text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C2410C] disabled:opacity-40">
                      {savingVoice ? <CircleNotch size={15} className="animate-spin" /> : <Star size={15} weight={voice === defaultVoice ? "fill" : "regular"} style={{ color: "#C2410C" }} />}
                      {voice === defaultVoice ? "Default" : "Set default"}
                    </button>
                    <button onClick={narrate} disabled={narrating || !hasTranscript} data-testid="narrate-btn"
                      title={hasTranscript ? "" : "Generate the transcript first"}
                      className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
                      style={{ background: "#C2410C" }}>
                      {narrating ? <CircleNotch size={16} className="animate-spin" /> : (narration ? <ArrowsClockwise size={16} /> : <SpeakerHigh size={16} weight="fill" />)}
                      {narrating ? "Generating…" : (narration ? "Regenerate" : "Generate narration")}
                    </button>
                  </div>
                </div>

                {narration ? (
                  <div className="mt-4" data-testid="narration-player">
                    <div className="text-xs text-slate-400 mb-2">
                      {narration.voice_name} · {Math.floor((narration.duration || 0) / 60)}m {Math.round((narration.duration || 0) % 60)}s · captions included · generated {fmtDate(narration.generated_at)}
                    </div>
                    {audioUrl ? (
                      <audio controls className="w-full" data-testid="narration-audio" src={audioUrl} crossOrigin="anonymous">
                        {capUrl && <track kind="captions" srcLang="en" label="English" src={capUrl} default />}
                      </audio>
                    ) : (
                      <div className="text-sm text-slate-400 flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Loading audio…</div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-3">
                    {hasTranscript
                      ? "Pick a voice and generate a natural voiceover of the transcript, with synced captions."
                      : "Generate the guide's assets first — narration reads the transcript aloud."}
                  </p>
                )}
              </div>

              {/* Walkthrough video */}
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="video-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FilmSlate size={18} className="text-[#C2410C]" weight="fill" />
                    <h3 className="text-base font-bold text-navy">Walkthrough video</h3>
                    {video?.status === "ready" && (
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${video.stale ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {video.stale ? "Out of date" : "Up to date"}
                      </span>
                    )}
                    {video?.status === "generating" && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Rendering…</span>
                    )}
                    {video?.status === "failed" && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">Failed</span>
                    )}
                  </div>
                  <button onClick={generateVideo} disabled={videoBusy || !narration} data-testid="generate-video-btn"
                    title={narration ? "" : "Generate the narration first"}
                    className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
                    style={{ background: "#C2410C" }}>
                    {videoBusy ? <CircleNotch size={16} className="animate-spin" /> : (video?.status === "ready" ? <ArrowsClockwise size={16} /> : <FilmSlate size={16} weight="fill" />)}
                    {videoBusy ? "Rendering…" : (video?.status === "ready" ? "Regenerate" : "Generate video")}
                  </button>
                </div>

                {video?.status === "ready" && (
                  <button onClick={toggleFeature} data-testid="feature-video-btn"
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg mt-3 border transition-colors ${video.featured ? "bg-amber-50 border-amber-300 text-amber-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    <Star size={14} weight={video.featured ? "fill" : "regular"} style={video.featured ? { color: "#F59E0B" } : {}} />
                    {video.featured ? "Featured as ‘Start here’" : "Set as ‘Start here’"}
                  </button>
                )}


                {video?.status === "ready" && selectedSlug ? (
                  <div className="mt-4" data-testid="video-player">
                    <div className="text-xs text-slate-400 mb-2">
                      Oversized cursor + highlighted actions on real screens · {Math.floor((video.duration || 0) / 60)}m {Math.round((video.duration || 0) % 60)}s · generated {fmtDate(video.generated_at)}
                    </div>
                    <video controls playsInline preload="metadata" controlsList="nodownload noremoteplayback"
                      disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
                      className="w-full rounded-xl bg-black"
                      data-testid="video-preview" src={`${API}/guides/${selectedSlug}/walkthrough/video`} />
                    <div className="mt-4">
                      <SocialClipStudio slug={selectedSlug} />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-3">
                    {narration
                      ? "Renders a screencast that shows the steps on real Lottie screens, timed to the narration, with an oversized cursor and highlighted actions."
                      : "Generate the narration first — the video is timed to the voiceover."}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* How-to video preview modal */}
      <Dialog open={!!howtoPreview} onOpenChange={(o) => { if (!o) setHowtoPreview(null); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-navy border-navy" data-testid="howto-preview-modal">
          {howtoPreview && (
            <>
              <DialogTitle className="sr-only">{howtoPreview.title}</DialogTitle>
              <DialogDescription className="sr-only">Preview of the {howtoPreview.title} walkthrough</DialogDescription>
              <video key={howtoPreview.slug} data-testid="howto-preview-player"
                src={`${API}/learning/howto/${howtoPreview.slug}/video`}
                controls autoPlay playsInline controlsList="nodownload noremoteplayback"
                disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
                className="w-full aspect-video bg-black" />
              <div className="px-5 py-4">
                <div className="font-heading font-bold text-white leading-tight mb-3">{howtoPreview.title}</div>
                <SocialClipStudio slug={howtoPreview.slug} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit narration script modal */}
      <Dialog open={!!scriptEdit} onOpenChange={(o) => { if (!o) setScriptEdit(null); }}>
        <DialogContent className="max-w-2xl" data-testid="howto-script-modal">
          {scriptEdit && (
            <>
              <DialogTitle>Edit walkthrough script</DialogTitle>
              <DialogDescription>{scriptEdit.title} — saving regenerates the narration and video from your script (about a minute).</DialogDescription>
              {scriptLoading ? (
                <div className="py-10 text-sm text-slate-500 flex items-center gap-2"><CircleNotch className="animate-spin" /> Loading script…</div>
              ) : (
                <>
                  <textarea value={scriptText} onChange={(e) => setScriptText(e.target.value)}
                    data-testid="howto-script-textarea" rows={12}
                    className="w-full mt-2 rounded-xl border border-slate-200 p-3 text-sm text-navy leading-relaxed focus:outline-none focus:ring-2 focus:ring-orangeink/40"
                    placeholder="The narration script the voiceover will read…" />
                  <div className="flex items-center justify-between gap-2 mt-3">
                    <button onClick={polishScript} disabled={scriptPolishing || scriptSaving} data-testid="howto-script-polish"
                      className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 text-navy hover:border-orangeink disabled:opacity-60">
                      {scriptPolishing ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" style={{ color: "#C2410C" }} />} Polish with AI
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setScriptEdit(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-slate-200 text-slate-600">Cancel</button>
                      <button onClick={saveScript} disabled={scriptSaving} data-testid="howto-script-save"
                        className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-60" style={{ background: "#C2410C" }}>
                        {scriptSaving ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} />} Save &amp; regenerate
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit chapter markers modal */}
      <Dialog open={!!chapterEdit} onOpenChange={(o) => { if (!o) setChapterEdit(null); }}>
        <DialogContent className="max-w-lg" data-testid="howto-chapters-modal">
          {chapterEdit && (
            <>
              <DialogTitle>Edit chapters</DialogTitle>
              <DialogDescription>{chapterEdit.title} — drag to reorder, and rename or re-time the markers learners jump to. Times are mm:ss.</DialogDescription>
              {chapterLoading ? (
                <div className="py-10 text-sm text-slate-500 flex items-center gap-2"><CircleNotch className="animate-spin" /> Loading chapters…</div>
              ) : (
                <>
                  <div className="mt-2 space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="chapter-rows">
                    {chapterList.map((c, i) => (
                      <div key={c._id} data-testid={`chapter-row-${i}`}
                        draggable
                        onDragStart={() => { dragIndexRef.current = i; }}
                        onDragOver={(e) => { e.preventDefault(); if (dragOverIdx !== i) setDragOverIdx(i); }}
                        onDrop={(e) => { e.preventDefault(); moveChapter(dragIndexRef.current, i); dragIndexRef.current = null; setDragOverIdx(null); }}
                        onDragEnd={() => { dragIndexRef.current = null; setDragOverIdx(null); }}
                        className={`rounded-lg transition-colors p-1 ${dragOverIdx === i ? "bg-orange-50 ring-1 ring-orangeink/40" : ""}`}>
                        <div className="flex items-center gap-2">
                          <span data-testid={`chapter-drag-${i}`}
                            className="shrink-0 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing p-1" title="Drag to reorder">
                            <DotsSixVertical size={16} weight="bold" />
                          </span>
                          <input value={c._t} onChange={(e) => updateChapter(i, "_t", e.target.value)}
                            data-testid={`chapter-time-${i}`} placeholder="0:00"
                            className="w-16 shrink-0 rounded-lg border border-slate-200 px-2 py-2 text-sm font-mono text-navy text-center focus:outline-none focus:ring-2 focus:ring-orangeink/40" />
                          <input value={c.label} onChange={(e) => updateChapter(i, "label", e.target.value)}
                            data-testid={`chapter-label-${i}`} placeholder="Chapter title"
                            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orangeink/40" />
                          <button onClick={() => removeChapter(i)} data-testid={`chapter-remove-${i}`}
                            className="shrink-0 text-slate-400 hover:text-red-500 transition-colors p-1.5">
                            <Trash size={15} />
                          </button>
                        </div>
                        {chapterEdit.kind === "marketing" && (
                          <input value={c.desc || ""} onChange={(e) => updateChapter(i, "desc", e.target.value)}
                            data-testid={`chapter-desc-${i}`} placeholder="Step description (shown on the homepage)"
                            className="mt-1.5 ml-8 w-[calc(100%-2.5rem)] rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-orangeink/40" />
                        )}
                      </div>
                    ))}
                    {chapterList.length === 0 && (
                      <div className="text-sm text-slate-500 py-4 text-center">No chapters yet — add one below.</div>
                    )}
                  </div>
                  <button onClick={addChapter} data-testid="chapter-add"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-orangeink hover:opacity-80 transition-opacity">
                    <Plus size={14} weight="bold" /> Add chapter
                  </button>
                  <div className="flex items-center justify-end gap-2 mt-4">
                    <button onClick={() => setChapterEdit(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-slate-200 text-slate-600">Cancel</button>
                    <button onClick={saveChapters} disabled={chapterSaving} data-testid="chapter-save"
                      className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-60" style={{ background: "#C2410C" }}>
                      {chapterSaving ? <CircleNotch size={15} className="animate-spin" /> : <ListNumbers size={15} />} Save chapters
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

// small wrapper to avoid an extra import at top when icon set already large
function GraduationCapWrap() {
  return <Sparkle size={13} weight="fill" />;
}

